import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  useTheme,
  alpha,
  LinearProgress
} from '@mui/material';
import {
  Shield as ShieldIcon,
  Email as EmailIcon,
  Key as KeyIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  // Error as ErrorIcon,
  AccessTime as AccessTimeIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { OTP_CONFIG, sendOTP, verifyOTP, resendOTP } from '../config/otpConfig';

const OTPModal = ({
  open,
  onClose,
  actionType,
  itemData,
  onVerifySuccess,
  onActionExecute // Callback to execute the actual action
}) => {
  const theme = useTheme();
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(OTP_CONFIG.OTP_EXPIRY_TIME);
  const [otpSent, setOtpSent] = useState(false);
  const [verificationInProgress, setVerificationInProgress] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const otpTimerRef = useRef(null);

  // Timer effect
  useEffect(() => {
    if (!open || !otpSent) return;

    otpTimerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(otpTimerRef.current);
          handleOTPExpired();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (otpTimerRef.current) {
        clearInterval(otpTimerRef.current);
      }
    };
  }, [open, otpSent]);

  // Initialize OTP when modal opens
  useEffect(() => {
    const initializeOTP = async () => {
      setLoading(true);
      setError('');

      try {
        const result = await sendOTP(actionType, itemData);

        if (result.success) {
          setOtpSent(true);
          setTimeLeft(OTP_CONFIG.OTP_EXPIRY_TIME);
          setSuccessMessage(result.message);
        } else {
          setError(result.message);
        }
      } catch (err) {
        setError('Failed to send OTP. Please try again.');
        console.error('OTP sending error:', err);
      } finally {
        setLoading(false);
      }
    };
    
    if (open && actionType && itemData) {
      initializeOTP();
    } else {
      // Reset state when modal closes
      setOtp('');
      setError('');
      setTimeLeft(OTP_CONFIG.OTP_EXPIRY_TIME);
      setOtpSent(false);
      setSuccessMessage('');
    }
  }, [open, actionType, itemData]);

  // Clear timer on unmount
  useEffect(() => {
    return () => {
      if (otpTimerRef.current) {
        clearInterval(otpTimerRef.current);
      }
    };
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVerify = async () => {
    if (!otp || otp.length !== OTP_CONFIG.OTP_LENGTH) {
      setError(`Please enter a valid ${OTP_CONFIG.OTP_LENGTH}-digit OTP`);
      return;
    }

    setVerificationInProgress(true);
    setError('');

    try {
      const result = await verifyOTP(otp, actionType, itemData);

      if (result.success) {
        setSuccessMessage('OTP verified successfully!');

        // Store pending action in localStorage (like HTML version)
        localStorage.setItem(OTP_CONFIG.STORAGE_KEYS.PENDING_ACTION, JSON.stringify({
          type: actionType,
          itemData: itemData,
          timestamp: new Date().toISOString()
        }));

        if (itemData) {
          localStorage.setItem(OTP_CONFIG.STORAGE_KEYS.PENDING_ITEM, JSON.stringify(itemData));
        }

        // Execute the action after successful verification
        if (onVerifySuccess) {
          onVerifySuccess();
        }

        // Close modal after delay
        setTimeout(() => {
          handleCloseModal();
        }, 1500);

      } else {
        setError(result.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
      console.error('OTP verification error:', err);
    } finally {
      setVerificationInProgress(false);
    }
  };

  const handleResendOTP = async () => {
    if (timeLeft > 30) {
      setError(`Please wait ${formatTime(timeLeft - 30)} before resending`);
      return;
    }

    setResendLoading(true);
    setError('');

    try {
      const result = await resendOTP(actionType, itemData);

      if (result.success) {
        setTimeLeft(OTP_CONFIG.OTP_EXPIRY_TIME);
        setOtp('');
        setOtpSent(true);
        setSuccessMessage(result.message);

        // Restart timer
        if (otpTimerRef.current) {
          clearInterval(otpTimerRef.current);
        }

        otpTimerRef.current = setInterval(() => {
          setTimeLeft(prev => {
            if (prev <= 1) {
              clearInterval(otpTimerRef.current);
              handleOTPExpired();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

      } else {
        setError(result.message || 'Failed to resend OTP');
      }
    } catch (err) {
      setError('Failed to resend OTP. Please try again.');
      console.error('OTP resend error:', err);
    } finally {
      setResendLoading(false);
    }
  };

  const handleOTPExpired = () => {
    setError('OTP has expired. Please request a new one.');
    setOtpSent(false);
  };

  const handleCloseModal = () => {
    // Clear localStorage when modal closes (like HTML version)
    localStorage.removeItem(OTP_CONFIG.STORAGE_KEYS.PENDING_ACTION);
    localStorage.removeItem(OTP_CONFIG.STORAGE_KEYS.PENDING_ITEM);
    localStorage.removeItem(OTP_CONFIG.STORAGE_KEYS.PENDING_OTP);

    // Clear timer
    if (otpTimerRef.current) {
      clearInterval(otpTimerRef.current);
    }

    // Reset state
    setOtp('');
    setError('');
    setTimeLeft(OTP_CONFIG.OTP_EXPIRY_TIME);
    setOtpSent(false);
    setSuccessMessage('');

    onClose();
  };

  const getActionDetails = () => {
    switch (actionType) {
      case 'edit':
        return {
          title: 'Edit Item',
          description: `Editing item: ${itemData?.updated?.serialNo || itemData?.serialNo || ''}`,
          color: theme.palette.primary.main,
          icon: <EditIcon />
        };
      case 'delete':
        return {
          title: 'Delete Item',
          description: `Deleting item: ${itemData?.serialNo || itemData?.name || ''}`,
          color: theme.palette.error.main,
          icon: <DeleteIcon />
        };
      default:
        return {
          title: 'Admin Verification',
          description: 'This action requires admin verification',
          color: theme.palette.primary.main,
          icon: <ShieldIcon />
        };
    }
  };

  const actionDetails = getActionDetails();

  // Auto-submit when 6 digits are entered (like HTML version)
  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
    setError('');

    if (value.length === 6) {
      handleVerify();
    }
  };

  // Calculate timer progress percentage
  const timerProgress = (timeLeft / OTP_CONFIG.OTP_EXPIRY_TIME) * 100;

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={verificationInProgress || loading}
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.mode === 'light'
            ? 'rgba(255, 255, 255, 0.98)'
            : 'rgba(30, 41, 59, 0.98)',
          backdropFilter: 'blur(20px)',
          border: '1px solid',
          borderColor: alpha(theme.palette.divider, 0.3),
          borderRadius: '16px',
          boxShadow: theme.shadows[10]
        }
      }}
    >
      <DialogTitle sx={{
        padding: '20px 24px 10px',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${alpha(actionDetails.color, 0.2)} 0%, ${alpha(actionDetails.color, 0.1)} 100%)`,
                marginRight: 2,
                border: `1px solid ${alpha(actionDetails.color, 0.3)}`
              }}
            >
              <ShieldIcon sx={{ color: actionDetails.color, fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="bold" sx={{ color: 'text.primary' }}>
                Admin Verification Required
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {actionDetails.title}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleCloseModal}
            size="small"
            disabled={verificationInProgress || loading}
            sx={{
              backgroundColor: alpha(theme.palette.text.secondary, 0.1),
              '&:hover': {
                backgroundColor: alpha(theme.palette.text.secondary, 0.2)
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: '20px 24px' }}>
        {/* Success Message */}
        {successMessage && (
          <Alert
            severity="success"
            icon={<CheckIcon />}
            sx={{
              marginBottom: 3,
              backgroundColor: alpha(theme.palette.success.main, 0.1),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              borderRadius: '12px',
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <Typography variant="body2">
              {successMessage}
            </Typography>
          </Alert>
        )}

        {/* OTP Sent Message */}
        {otpSent && !successMessage && (
          <Alert
            severity="info"
            icon={<EmailIcon />}
            sx={{
              marginBottom: 3,
              backgroundColor: alpha(theme.palette.info.main, 0.1),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              borderRadius: '12px'
            }}
          >
            <Typography variant="body2">
              OTP has been sent to {OTP_CONFIG.ADMIN_EMAIL}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Please check your email and enter the OTP below
            </Typography>
          </Alert>
        )}

        {/* Action Preview */}
        <Box
          sx={{
            padding: '16px',
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            border: `1px solid ${alpha(actionDetails.color, 0.2)}`,
            borderRadius: '12px',
            marginBottom: 3,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '4px',
              height: '100%',
              backgroundColor: actionDetails.color
            }}
          />
          <Box sx={{ marginLeft: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom display="flex" alignItems="center">
              <WarningIcon sx={{ marginRight: 1, fontSize: 16, color: actionDetails.color }} />
              Action Details:
            </Typography>
            <Typography variant="body2" color="text.primary" gutterBottom>
              {actionDetails.description}
            </Typography>
            {actionType === 'edit' && itemData?.original && (
              <Typography variant="caption" color="text.secondary">
                Changes will be logged for audit purposes
              </Typography>
            )}
          </Box>
        </Box>

        {/* OTP Input */}
        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom display="flex" alignItems="center">
            <KeyIcon sx={{ marginRight: 1, fontSize: 16 }} />
            Enter 6-digit OTP
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter 6-digit OTP"
            value={otp}
            onChange={handleOtpChange}
            inputProps={{
              maxLength: 6,
              inputMode: 'numeric',
              pattern: '[0-9]*',
              style: {
                textAlign: 'center',
                fontSize: '20px',
                fontWeight: 'bold',
                letterSpacing: '8px'
              }
            }}
            error={!!error}
            helperText={error}
            disabled={loading || verificationInProgress || !otpSent}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: alpha(theme.palette.background.paper, 0.6),
                height: '56px'
              }
            }}
          />
        </Box>

        {/* Timer with Progress Bar */}
        <Box mb={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              padding: '8px 16px',
              backgroundColor: timeLeft < 30
                ? alpha(theme.palette.error.main, 0.1)
                : alpha(theme.palette.warning.main, 0.1),
              border: `1px solid ${timeLeft < 30
                  ? alpha(theme.palette.error.main, 0.2)
                  : alpha(theme.palette.warning.main, 0.2)
                }`,
              borderRadius: '12px',
              marginBottom: 1
            }}
          >
            <Box display="flex" alignItems="center">
              <AccessTimeIcon sx={{
                marginRight: 1,
                fontSize: 16,
                color: timeLeft < 30 ? theme.palette.error.main : theme.palette.warning.main
              }} />
              <Typography variant="body2" fontWeight="bold">
                OTP expires in:
              </Typography>
            </Box>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{
                color: timeLeft < 30 ? theme.palette.error.main : theme.palette.warning.main,
                fontFamily: 'monospace'
              }}
            >
              {formatTime(timeLeft)}
            </Typography>
          </Box>

          <LinearProgress
            variant="determinate"
            value={timerProgress}
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.divider, 0.2),
              '& .MuiLinearProgress-bar': {
                backgroundColor: timeLeft < 30 ? theme.palette.error.main : theme.palette.warning.main,
                borderRadius: 2
              }
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{
        padding: '16px 24px 20px',
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        gap: 1
      }}>
        <Button
          onClick={handleCloseModal}
          disabled={verificationInProgress || loading}
          startIcon={<CloseIcon />}
          sx={{
            color: 'text.secondary',
            borderRadius: '10px',
            padding: '8px 20px',
            minWidth: '100px'
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleResendOTP}
          disabled={resendLoading || verificationInProgress || loading || timeLeft > 30}
          startIcon={<RefreshIcon />}
          sx={{
            color: theme.palette.info.main,
            borderRadius: '10px',
            padding: '8px 20px',
            minWidth: '120px',
            backgroundColor: alpha(theme.palette.info.main, 0.1),
            '&:hover': {
              backgroundColor: alpha(theme.palette.info.main, 0.2)
            }
          }}
        >
          {resendLoading ? <CircularProgress size={20} /> : 'Resend OTP'}
        </Button>

        <Button
          onClick={handleVerify}
          disabled={verificationInProgress || loading || !otp || otp.length !== 6 || timeLeft === 0}
          variant="contained"
          startIcon={verificationInProgress ?
            <CircularProgress size={20} color="inherit" /> :
            <CheckCircleIcon />
          }
          sx={{
            background: `linear-gradient(135deg, ${actionDetails.color} 0%, ${alpha(actionDetails.color, 0.8)} 100%)`,
            borderRadius: '10px',
            padding: '8px 24px',
            minWidth: '160px',
            fontWeight: 'bold',
            boxShadow: theme.shadows[3],
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: theme.shadows[5]
            },
            '&:disabled': {
              background: alpha(theme.palette.text.disabled, 0.2),
              color: alpha(theme.palette.text.disabled, 0.5)
            }
          }}
        >
          {verificationInProgress ? 'Verifying...' : 'Verify & Proceed'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Add these missing icon imports
const EditIcon = (props) => (
  <svg {...props} fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
  </svg>
);

const DeleteIcon = (props) => (
  <svg {...props} fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
  </svg>
);

export default OTPModal;